import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Users, Star, Award, Briefcase, CheckCircle2, ArrowRight, Phone, Search } from 'lucide-react';

const SAMTalents = () => {
  const categories = [
    {
      icon: <Briefcase className="w-12 h-12 text-[#27AE60]" />,
      title: 'Business Professionals',
      description: 'Consultants, advisors, accountants, and legal experts ready to support your business needs.',
    },
    {
      icon: <Star className="w-12 h-12 text-[#3498DB]" />,
      title: 'Creative Specialists',
      description: 'Designers, marketers, content creators, and brand strategists for your creative projects.',
    },
    {
      icon: <Users className="w-12 h-12 text-[#27AE60]" />,
      title: 'Family Services',
      description: 'Tutors, coaches, activities organizers, and childcare professionals for your family.',
    },
    {
      icon: <Award className="w-12 h-12 text-[#3498DB]" />,
      title: 'Technical Experts',
      description: 'Developers, IT specialists, engineers, and technical consultants for your tech needs.',
    },
  ];

  const advantages = [
    'Pre-vetted and qualified professionals',
    'Flexible engagement models (project-based, hourly, retainer)',
    'Direct communication with talents',
    'Transparent pricing and contracts',
    'Quality assurance and satisfaction guarantee',
    'Fast matching within 48 hours',
    'Ongoing support and project management',
    'Wide range of expertise across industries',
  ];

  return (
    <div className="min-h-screen" data-testid="sam-talents-page">
      {/* Hero Section */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="talents-hero"
      >
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-block bg-[#27AE60]/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-[#2ECC71] font-semibold">SAM Talents</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="talents-hero-title">
              Connect with <span className="text-[#2ECC71]">Experienced Professionals</span>
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto leading-relaxed">
              Your platform to find qualified and talented freelancers for all your business and family needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="overview-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <img
                src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?w=800"
                alt="Talented Professionals"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2"
            >
              <h2 className="text-4xl md:text-5xl font-semibold mb-6 text-[#2C3E50]">
                What is SAM Talents?
              </h2>
              <div className="gold-line mb-6" />
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  SAM Talents is a curated platform connecting businesses and families in Dubai with experienced, qualified, and talented freelance professionals across various domains.
                </p>
                <p>
                  We bring together a community of vetted experts offering ALL-IN-ONE services for both professional projects and family activities, making it easy to find the right talent for any need.
                </p>
                <p>
                  From business consultants to creative specialists, from tutors to event organizers, SAM Talents provides access to top-tier professionals who understand the Dubai market and deliver exceptional results.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-20 bg-gradient-to-br from-[#F4F4F5] to-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-[#2C3E50]">
              Talent Categories
            </h2>
            <div className="gold-line mx-auto" />
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Find the perfect professional for your specific needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="service-card"
                data-testid={`category-${index}`}
              >
                <div className="mb-4">{category.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-[#2C3E50]">{category.title}</h3>
                <p className="text-gray-700 leading-relaxed">{category.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="advantages-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-4 text-[#2C3E50]">
              Why Choose SAM Talents?
            </h2>
            <div className="gold-line mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="flex items-start gap-4 p-6 bg-gradient-to-br from-white to-[#F4F4F5] rounded-xl border border-gray-200 hover:border-[#27AE60]/50 transition-all"
                data-testid={`advantage-${index}`}
              >
                <CheckCircle2 className="w-6 h-6 text-[#3498DB] flex-shrink-0 mt-1" />
                <span className="text-gray-700 text-lg">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#27AE60] to-[#2ECC71] text-white" data-testid="talents-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold mb-6">
              Find Your Perfect Match Today
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Tell us what you need, and we'll connect you with the right talent within 48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary ripple inline-flex items-center justify-center gap-2">
                <Search size={18} />
                Find a Talent
              </Link>
              <a
                href="tel:+971563528126"
                className="bg-white text-[#27AE60] hover:bg-gray-100 px-8 py-4 rounded-xl font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2 ripple"
              >
                <Phone size={18} />
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SAMTalents;